import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  message,
  Modal,
  Dropdown,
} from "antd";
import dayjs from "dayjs";
import { PrinterOutlined } from "@ant-design/icons";
import api from "../../services/apiClient";
import {
  frontDeskService,
  adminRoomService,
  adminBookingService,
  offersService,
  reservationPrintService,
} from "../../services/adminService";
import { isRoomUnavailable, getErrorMessage, canForceOverlap, promptForceReason } from "../../utils/overlap";
import { deriveGroupConfirmationNumber } from "../../utils/groupReference";
import roomPackageService from "../../services/roomPackageService";
import "./FrontDesk.css";

import { FrontDeskBooking, GuestProfile, Offer, RoomPackage } from "../../types";
import { CreateBookingModal } from "./components/CreateBookingModal";
import { EditBookingModal } from "./components/EditBookingModal";
import { CheckInModal } from "./components/CheckInModal";
import { CheckOutModal } from "./components/CheckOutModal";
import { AllocateModal } from "./components/AllocateModal";
import { ExtendStayModal } from "./components/ExtendStayModal";
import { ChangeRoomModal } from "./components/ChangeRoomModal";
import { ViewBookingModal } from "./components/ViewBookingModal";
import { ViewGuestProfileModal } from "./components/ViewGuestProfileModal";
import { RoomCheckOutInspectionModal } from "./components/RoomCheckOutInspectionModal";
import { DepositPaymentModal } from "./components/DepositPaymentModal";
import DatePrintModal from "../reservations/DatePrintModal";
import ReservationCalendar from "../reservations/ReservationCalendar";
import {
  getBookingColumns,
  getGuestColumns,
} from "./columns";

const { RangePicker } = DatePicker;

const FrontDesk: React.FC = () => {

  const [bookings, setBookings] = useState<FrontDeskBooking[]>([]);
  const [guestProfiles, setGuestProfiles] = useState<GuestProfile[]>([]);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [roomPackages, setRoomPackages] = useState<RoomPackage[]>([]);

  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [search, setSearch] = useState("");
  const [bookingDateRange, setBookingDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | null
  >(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState<any>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createActiveTab, setCreateActiveTab] = useState("1");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editActiveTab, setEditActiveTab] = useState("1");
  const [allocateModalOpen, setAllocateModalOpen] = useState(false);
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [checkOutModalOpen, setCheckOutModalOpen] = useState(false);
  const [extendStayModalOpen, setExtendStayModalOpen] = useState(false);
  const [selectedExtendBooking, setSelectedExtendBooking] = useState<FrontDeskBooking | null>(null);
  const [changeRoomModalOpen, setChangeRoomModalOpen] = useState(false);
  const [selectedChangeRoomBooking, setSelectedChangeRoomBooking] = useState<FrontDeskBooking | null>(null);
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [selectedDepositBooking, setSelectedDepositBooking] = useState<FrontDeskBooking | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewGuestModalOpen, setViewGuestModalOpen] = useState(false);
  const [selectedGuestProfile, setSelectedGuestProfile] = useState<GuestProfile | null>(null);
  const [selectedBooking, setSelectedBooking] =
    useState<FrontDeskBooking | null>(null);
  const [selectedViewBooking, setSelectedViewBooking] = 
    useState<(FrontDeskBooking & { groupChildren?: FrontDeskBooking[], is_group_parent?: boolean }) | null>(null);
  const [selectedEditBooking, setSelectedEditBooking] =
    useState<FrontDeskBooking | null>(null);


  const [selectedCheckInBooking, setSelectedCheckInBooking] =
    useState<FrontDeskBooking | null>(null);
  const [selectedCheckOutBooking, setSelectedCheckOutBooking] =
    useState<FrontDeskBooking | null>(null);
  const [roomCheckModalOpen, setRoomCheckModalOpen] = useState(false);
  const [selectedRoomCheckBooking, setSelectedRoomCheckBooking] =
    useState<FrontDeskBooking | null>(null);

  const [printModalType, setPrintModalType] = useState<"arrivals" | "departures" | null>(null);
  const [printLoading, setPrintLoading] = useState(false);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [allocateForm] = Form.useForm();
  const [checkInForm] = Form.useForm();
  const [checkOutForm] = Form.useForm();

  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [roomAvailabilities, setRoomAvailabilities] = useState<
    Record<string, { isAvailable: boolean; availableCount: number; acCount?: number; nonAcCount?: number; bothCount?: number; }>
  >({});


  const createGuestName = Form.useWatch("guest_name", createForm);
  const createReservationSource = Form.useWatch(
    "reservation_source",
    createForm,
  );
  const roomsList = Form.useWatch("rooms", createForm) || [];
  const createCheckInDate = Form.useWatch("check_in_date", createForm);
  const createCheckOutDate = Form.useWatch("check_out_date", createForm);

  const getBestActiveOffer = useCallback((roomTypeId: string, checkInDate: string | Date, targetAcStatus?: string) => {
    const activeOffersForRoom = offers.filter(
      (o) =>
        o.room_type_id === roomTypeId &&
        o.is_active &&
        new Date(o.start_date) <= new Date(checkInDate) &&
        new Date(o.end_date) >= new Date(checkInDate) &&
        !o.promo_code
    );

    if (targetAcStatus) {
      const exactMatch = activeOffersForRoom.find((o) => o.ac_status === targetAcStatus);
      if (exactMatch) return exactMatch;
    }

    return activeOffersForRoom.find((o) => !o.ac_status || o.ac_status === "any") || null;
  }, [offers]);

  const isCreateValid = useMemo(() => {
    if (
      !createGuestName ||
      !createReservationSource ||
      !createCheckInDate ||
      !createCheckOutDate ||
      !roomsList ||
      roomsList.length === 0
    )
      return false;

    // Check if every room has a type and quantity > 0
    if (
      roomsList.some(
        (r: any) => !r || !r.room_type_id || !r.quantity || r.quantity <= 0,
      )
    )
      return false;

    // Check availability constraints
    const requestedCounts: Record<string, { total: number; ac: number; non_ac: number; both: number }> = {};
    for (const r of roomsList) {
      if (r && r.room_type_id && r.quantity) {
        if (!requestedCounts[r.room_type_id]) {
          requestedCounts[r.room_type_id] = { total: 0, ac: 0, non_ac: 0, both: 0 };
        }
        requestedCounts[r.room_type_id].total += r.quantity;
        if (r.ac_status === 'ac') requestedCounts[r.room_type_id].ac += r.quantity;
        else if (r.ac_status === 'non_ac') requestedCounts[r.room_type_id].non_ac += r.quantity;
        else requestedCounts[r.room_type_id].both += r.quantity;
      }
    }

    for (const [rtId, counts] of Object.entries(requestedCounts)) {
      const avail = roomAvailabilities[rtId];
      if (!avail || !avail.isAvailable || avail.availableCount < counts.total) {
        return false;
      }

      if (avail.acCount !== undefined && avail.nonAcCount !== undefined && avail.bothCount !== undefined) {
        let rAc = avail.acCount;
        let rNonAc = avail.nonAcCount;
        let rBoth = avail.bothCount;
        let reqAc = counts.ac;
        let reqNonAc = counts.non_ac;
        let reqBoth = counts.both;

        if (reqAc <= rAc) rAc -= reqAc;
        else { const overflow = reqAc - rAc; rAc = 0; rBoth -= overflow; }

        if (reqNonAc <= rNonAc) rNonAc -= reqNonAc;
        else { const overflow = reqNonAc - rNonAc; rNonAc = 0; rBoth -= overflow; }

        if (reqBoth <= rBoth) rBoth -= reqBoth;
        else {
          let overflow = reqBoth - rBoth;
          rBoth = 0;
          if (overflow <= rAc) { rAc -= overflow; overflow = 0; }
          else { overflow -= rAc; rAc = 0; }

          if (overflow > 0) {
            if (overflow <= rNonAc) { rNonAc -= overflow; overflow = 0; }
            else { overflow -= rNonAc; rNonAc = 0; }
          }
          if (overflow > 0) return false;
        }

        if (rBoth < 0 || rAc < 0 || rNonAc < 0) return false;
      }
    }

    return true;
  }, [
    createGuestName,
    createReservationSource,
    createCheckInDate,
    createCheckOutDate,
    roomsList,
    roomAvailabilities,
  ]);

  const editGuestName = Form.useWatch("guest_name", editForm);
  const editReservationSource = Form.useWatch("reservation_source", editForm);
  const editCheckInDate = Form.useWatch("check_in_date", editForm);
  const editCheckOutDate = Form.useWatch("check_out_date", editForm);
  const editRoomsList = Form.useWatch("rooms", editForm) || [];

  const isEditValid = useMemo(() => {
    const errors: string[] = [];

    if (!editGuestName) errors.push("Guest name is required");
    if (!editReservationSource) errors.push("Reservation source is required");
    if (!editCheckInDate) errors.push("Check-in date is required");
    if (!editCheckOutDate) errors.push("Check-out date is required");
    
    if (!editRoomsList || editRoomsList.length === 0) {
      errors.push("At least one room must be selected");
    } else {
      editRoomsList.forEach((r: any, index: number) => {
        if (!r || !r.room_type_id) {
          errors.push(`Room ${index + 1}: Room type is required`);
        } else if (!r.quantity || r.quantity <= 0) {
          errors.push(`Room ${index + 1}: Quantity must be at least 1`);
        }
      });
    }

    // Check availability constraints
    const requestedCounts: Record<string, number> = {};
    for (const r of editRoomsList || []) {
      if (r && r.room_type_id && r.quantity) {
        requestedCounts[r.room_type_id] =
          (requestedCounts[r.room_type_id] || 0) + r.quantity;
      }
    }

    for (const [rtId, count] of Object.entries(requestedCounts)) {
      const avail = roomAvailabilities[rtId];
      if (!avail) {
         errors.push(`Fetching availability for room type...`);
      } else if (!avail.isAvailable && (!selectedEditBooking || editCheckInDate?.format("YYYY-MM-DD") !== dayjs(selectedEditBooking.check_in_date).format("YYYY-MM-DD"))) {
         errors.push(`Selected room type is fully booked for the chosen dates`);
      } else if (avail.isAvailable && count > avail.availableCount && (!selectedEditBooking || editCheckInDate?.format("YYYY-MM-DD") !== dayjs(selectedEditBooking.check_in_date).format("YYYY-MM-DD"))) {
         errors.push(`Only ${avail.availableCount} rooms available, but ${count} requested`);
      }
    }

    return errors;
  }, [
    editGuestName,
    editReservationSource,
    editCheckInDate,
    editCheckOutDate,
    editRoomsList,
    roomAvailabilities,
    selectedEditBooking,
  ]);

  useEffect(() => {
    const uniqueRoomTypeIds = Array.from(
      new Set(roomsList.map((r: any) => r?.room_type_id).filter(Boolean)),
    ) as string[];

    if (uniqueRoomTypeIds.length === 0) {
      setBlockedDates([]);
      return;
    }

    const fetchRoomBlockedDates = async () => {
      try {
        const months: string[] = [];
        const start = dayjs();
        for (let i = 0; i < 6; i++) {
          months.push(start.add(i, "month").format("YYYY-MM"));
        }

        const allBlockedSets = await Promise.all(
          uniqueRoomTypeIds.map(async (rtId) => {
            const responses = await Promise.all(
              months.map(async (m) => {
                const res = await api.get(
                  `/rooms/availability-calendar-dates`,
                  {
                    params: { month: m, roomTypeId: rtId },
                  },
                );
                return res.data?.data?.fullyBookedDates || [];
              }),
            );
            return new Set(responses.flat() as string[]);
          }),
        );

        if (allBlockedSets.length > 0) {
          const firstSet = allBlockedSets[0];
          const intersection = Array.from(firstSet)
            .filter((date) => allBlockedSets.every((set) => set.has(date)))
            .sort();
          setBlockedDates(intersection);
        } else {
          setBlockedDates([]);
        }
      } catch (err) {
        console.error("Failed to fetch blocked dates:", err);
      }
    };

    void fetchRoomBlockedDates();
  }, [JSON.stringify(roomsList.map((r: any) => r?.room_type_id))]);

  useEffect(() => {
    // If edit modal is open, use edit values, else use create values
    const currentRoomsList = editModalOpen ? editRoomsList : (createModalOpen ? roomsList : []);
    const currentCheckInDate = editModalOpen ? editCheckInDate : (createModalOpen ? createCheckInDate : undefined);
    const currentCheckOutDate = editModalOpen ? editCheckOutDate : (createModalOpen ? createCheckOutDate : undefined);

    const uniqueRoomTypeIds = Array.from(
      new Set(currentRoomsList.map((r: any) => r?.room_type_id).filter(Boolean)),
    ) as string[];

    if (
      uniqueRoomTypeIds.length > 0 &&
      currentCheckInDate &&
      currentCheckOutDate
    ) {
      const fetchAvailability = async () => {
        const newAvailabilities: Record<string, any> = {};
        await Promise.all(
          uniqueRoomTypeIds.map(async (rtId) => {
            try {
              const res = await adminRoomService.getRoomTypeAvailability(rtId, {
                checkInDate: currentCheckInDate.format("YYYY-MM-DD"),
                checkOutDate: currentCheckOutDate.format("YYYY-MM-DD"),
              });
              newAvailabilities[rtId] = res.data || null;
            } catch (err) {
              console.error(
                `Failed to fetch room availability for ${rtId}:`,
                err,
              );
            }
          }),
        );
        setRoomAvailabilities(newAvailabilities);
      };
      void fetchAvailability();
    } else {
      setRoomAvailabilities({});
    }
  }, [
    createModalOpen,
    editModalOpen,
    JSON.stringify(roomsList.map((r: any) => r?.room_type_id)),
    createCheckInDate,
    createCheckOutDate,
    JSON.stringify(editRoomsList?.map((r: any) => r?.room_type_id)),
    editCheckInDate,
    editCheckOutDate,
  ]);

  const fetchInitial = async () => {
    try {
      setLoading(true);
      const [typesResponse, roomsResponse, offersResponse, guestsResponse] = await Promise.all([
        adminRoomService.getAllRoomTypes(),
        adminRoomService.getAllRooms(),
        offersService.getOffers(),
        frontDeskService.getGuestProfiles({}),
      ]);

      setRoomTypes(Array.isArray(typesResponse.data) ? typesResponse.data : []);
      setRooms(Array.isArray(roomsResponse.data) ? roomsResponse.data : []);
      setOffers(Array.isArray(offersResponse.data) ? offersResponse.data : []);
      setGuestProfiles(Array.isArray(guestsResponse.data) ? guestsResponse.data : []);
    } catch (error) {
      console.error(error);
      message.error("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomPackages = useCallback(async () => {
    try {
      const packagesResponse = await roomPackageService.getActive();
      setRoomPackages(Array.isArray(packagesResponse.data) ? packagesResponse.data : []);
    } catch (error) {
      console.error("Error fetching room packages:", error);
      setRoomPackages([]);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await frontDeskService.getBookings({
        status: statusFilter,
        search: search || undefined,
        date_from: bookingDateRange
          ? bookingDateRange[0].format("YYYY-MM-DD")
          : undefined,
        date_to: bookingDateRange
          ? bookingDateRange[1].format("YYYY-MM-DD")
          : undefined,
      });
      setBookings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch front desk bookings");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, bookingDateRange]);

  const fetchGuestProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await frontDeskService.getGuestProfiles({
        search: search || undefined,
      });
      setGuestProfiles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch guest profiles");
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchDashboard = useCallback(async () => {
    try {
      setDashboardLoading(true);
      const response: any = await frontDeskService.getDashboard();
      // `api.get` already unwraps to the response body ({ success, data }),
      // while a raw axios response wraps it in `data` — accept both, otherwise
      // the dashboard ends up null and every card renders 0.
      setDashboard(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch dashboard");
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchInitial();
  }, []);

  useEffect(() => {
    void fetchRoomPackages();
  }, [fetchRoomPackages]);

  useEffect(() => {
    if (createModalOpen) {
      void fetchRoomPackages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createModalOpen]);

  useEffect(() => {
    if (activeTab === "dashboard") {
      void fetchDashboard();
    } else if (activeTab === "bookings") {
      void fetchBookings();
    } else if (activeTab === "guestProfiles") {
      void fetchGuestProfiles();
    }
  }, [activeTab, fetchBookings, fetchDashboard, fetchGuestProfiles]);

  const groupedBookings = useMemo(() => {
    const calculateDynamicTotal = (b: any) => {
      const roomType = roomTypes.find((rt) => rt.id === b.room_type_id);
      if (!roomType) return Number(b.total_price || 0);

      const nights = Math.max(
        dayjs(b.check_out_date).diff(dayjs(b.check_in_date), "days"),
        1,
      );

      let basePricePerNight = b.ac_status === 'ac'
        ? Number(roomType.ac_base_price || roomType.base_price || 0)
        : b.ac_status === 'non_ac'
          ? Number(roomType.non_ac_base_price || roomType.base_price || 0)
          : Number(roomType.base_price || 0);
      const activeOffer = getBestActiveOffer(b.room_type_id, b.check_in_date, b.ac_status);

      if (activeOffer) {
        if (activeOffer.discount_amount) {
          basePricePerNight = Math.max(0, basePricePerNight - Number(activeOffer.discount_amount));
        } else if (activeOffer.discount_percentage) {
          basePricePerNight = Math.round(basePricePerNight * (1 - Number(activeOffer.discount_percentage) / 100));
        }
      }

      let roomTotal = basePricePerNight * nights;
      let extraBedsTotal = b.extra_beds ? Number(roomType.extra_bed_price || 0) * Number(b.extra_beds) * nights : 0;
      
      const vatRate = Number(b.vat_rate || 0);
      const earlyCheckInFee = Number((b as any).early_check_in_fee || 0);
      const lateCheckOutFee = Number((b as any).late_check_out_fee || 0);
      const storedServiceCharge = Number((b as any).service_charge || 0);
      const serviceChargePercentage = Number((b as any).service_charge_percentage || 0);
      const packageCharge = Number((b as any).package_charge || 0);

      const roomSubtotal = (b as any).room_subtotal ? Number((b as any).room_subtotal) : (roomTotal + extraBedsTotal);

      // Mirror the backend calculation order:
      // (room + package + fees) -> discount -> service charge -> VAT
      const totalBeforeDiscount = roomSubtotal + packageCharge + earlyCheckInFee + lateCheckOutFee;

      let manualDiscountDeduction = 0;
      const discountAmount = Number(b.discount_amount || 0);
      const discountPercentage = Number(b.discount_percentage || 0);

      if (discountAmount > 0) {
        manualDiscountDeduction = discountAmount;
      } else if (discountPercentage > 0) {
        manualDiscountDeduction = Math.round((totalBeforeDiscount * discountPercentage) / 100);
      }

      const afterDiscount = Math.max(0, totalBeforeDiscount - manualDiscountDeduction);

      const serviceCharge = storedServiceCharge > 0
        ? storedServiceCharge
        : serviceChargePercentage > 0
          ? Math.round((afterDiscount * serviceChargePercentage) / 100)
          : 0;

      const totalAfterService = afterDiscount + serviceCharge;
      const taxValue = (b as any).tax_amount !== undefined && (b as any).tax_amount !== null
        ? Number((b as any).tax_amount)
        : Math.round(totalAfterService * (vatRate / 100));

      return totalAfterService + taxValue;
    };

    const groups: Record<string, FrontDeskBooking[]> = {};
    const result: any[] = [];
    
    const augmentedBookings = bookings.map(b => ({
      ...b,
      // Keep the server-persisted total for authoritative amounts (deposit popup, etc.)
      server_total_price: Number((b as any).total_price || 0),
      total_price: calculateDynamicTotal(b)
    }));

    augmentedBookings.forEach(booking => {
      const gId = booking.group_id;
      if (!gId) {
        result.push(booking);
      } else {
        if (!groups[gId]) {
          groups[gId] = [];
        }
        groups[gId].push(booking);
      }
    });

    Object.entries(groups).forEach(([gId, groupBookings]) => {
      if (groupBookings.length === 1) {
        result.push(groupBookings[0]);
      } else {
        const first = groupBookings[0];
        const virtualParent = {
          ...first,
          id: first.id, // use real UUID for API calls
          is_group_parent: true,
          room_type_name: `Multiple (${groupBookings.length} rooms)`,
          room_number: '-',
          status: groupBookings.every(b => b.status === first.status) ? first.status : 'Mixed',
          total_price: groupBookings.reduce((sum, b) => sum + Number(b.total_price), 0),
          server_total_price: groupBookings.reduce(
            (sum, b) => sum + Number((b as any).server_total_price ?? b.total_price ?? 0),
            0,
          ),
          discount_amount: groupBookings.reduce((sum, b) => sum + Number(b.discount_amount || 0), 0),
          discount_percentage: Math.max(...groupBookings.map(b => Number(b.discount_percentage || 0))),
          early_check_in_fee: groupBookings.reduce((sum, b) => sum + Number((b as any).early_check_in_fee || 0), 0),
          late_check_out_fee: groupBookings.reduce((sum, b) => sum + Number((b as any).late_check_out_fee || 0), 0),
          service_charge: groupBookings.reduce((sum, b) => sum + Number((b as any).service_charge || 0), 0),
          deposit_amount: groupBookings.reduce(
            (sum, b) => sum + Number((b as any).deposit_amount || 0),
            0,
          ),
          deposit_paid: groupBookings.reduce(
            (sum, b) => sum + Number((b as any).deposit_paid || (b as any).paid_amount || 0),
            0,
          ),
          groupChildren: groupBookings.map(b => ({ ...b, is_group_child: true })), // The actual bookings
        };
        // The group's reference is the BASE number (FRO-280), never a
        // room-suffixed sibling (FRO-280-2). It has to match what the printed
        // confirmation shows. Row order is not reliable here — every room of
        // the group is inserted in one transaction and shares a created_at —
        // so derive it from the whole set instead of trusting `first`.
        virtualParent.confirmation_number =
          deriveGroupConfirmationNumber(
            groupBookings.map((b) => b.confirmation_number),
          ) ?? first.confirmation_number;
        result.push(virtualParent);
      }
    });
    
    // Maintain original sorting (by created_at descending)
    result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return result;
  }, [bookings, roomTypes, offers]);

  useEffect(() => {
    if (selectedViewBooking && groupedBookings.length > 0) {
      const updated = groupedBookings.find(b => b.id === selectedViewBooking.id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(selectedViewBooking)) {
        setSelectedViewBooking(updated as any);
      }
    }
  }, [groupedBookings, selectedViewBooking]);

  const handleCreateBooking = async (values: any) => {
    try {
      setLoading(true);
      const discountType = values.discount_type || "none";
      const discountValue = Number(values.discount_value || 0);
      const checkInFormatted = values.check_in_date.format("YYYY-MM-DD");
      const checkOutFormatted = values.check_out_date.format("YYYY-MM-DD");

      const enrichedRooms = (values.rooms || []).map((r: any) => {
        const roomType = roomTypes.find((rt) => rt.id === r.room_type_id);
        const originalPrice = r.ac_status === 'ac'
          ? Number(roomType?.ac_base_price || roomType?.base_price || 0)
          : r.ac_status === 'non_ac'
            ? Number(roomType?.non_ac_base_price || roomType?.base_price || 0)
            : Number(roomType?.base_price || 0);
        let discountedPrice = originalPrice;
        let offerTitle: string | null = null;

        const activeOffer = getBestActiveOffer(r.room_type_id, checkInFormatted, r.ac_status);

        if (activeOffer) {
          if (activeOffer.discount_amount) {
            discountedPrice = Math.max(0, discountedPrice - Number(activeOffer.discount_amount));
          } else if (activeOffer.discount_percentage) {
            discountedPrice = Math.round(discountedPrice * (1 - Number(activeOffer.discount_percentage) / 100));
          }
          offerTitle = activeOffer.title;
        }

        return {
          ...r,
          original_room_price: originalPrice,
          discounted_price: discountedPrice,
          offer_title: offerTitle,
        };
      });

      const firstRoom = enrichedRooms[0];
      const checkInDateObj = dayjs(checkInFormatted);
      const checkOutDateObj = dayjs(checkOutFormatted);
      const nights = Math.max(checkOutDateObj.diff(checkInDateObj, "days"), 1);

      let roomSubtotalForCalc = 0;
      enrichedRooms.forEach((r: any) => {
        const roomTypeForCalc = roomTypes.find((rt) => rt.id === r.room_type_id);
        const roomTotalForCalc = (r.discounted_price || 0) * nights * (r.quantity || 1);
        const extraBedsTotalForCalc = Number(r.extra_beds || 0) * Number(roomTypeForCalc?.extra_bed_price || 0) * nights;
        roomSubtotalForCalc += roomTotalForCalc + extraBedsTotalForCalc;
      });

      const earlyCheckInFeeCalc = Number(values.early_check_in_fee || 0);
      const lateCheckOutFeeCalc = Number(values.late_check_out_fee || 0);

      const selectedPkg = roomPackages.find(
        (p) => p.id === values.room_package_id,
      );
      const totalGuestsForPkg = enrichedRooms.reduce(
        (sum: number, r: any) =>
          sum + Number(r?.number_of_guests || 1) * Number(r?.quantity || 1),
        0,
      );
      let packageChargeForCalc = 0;
      if (selectedPkg) {
        const pcBase = Number(selectedPkg.base_price || 0);
        const pcPerPerson = Number(selectedPkg.price_per_person || 0);
        const pcG = Math.max(totalGuestsForPkg || 1, 1);
        const pcN = Math.max(nights || 1, 1);
        if (selectedPkg.charge_basis === "per_room_per_night")
          packageChargeForCalc = pcBase * pcN;
        else if (selectedPkg.charge_basis === "per_stay")
          packageChargeForCalc = pcBase;
        else if (selectedPkg.charge_basis === "per_person_per_stay")
          packageChargeForCalc = pcPerPerson * pcG;
        else if (selectedPkg.charge_basis === "per_person_per_night")
          packageChargeForCalc = pcPerPerson * pcG * pcN;
      }

      const serviceChargeType = values.service_charge_type || "none";
      const serviceChargeValue = Number(values.service_charge_value || 0);

      const total1 = roomSubtotalForCalc + packageChargeForCalc + earlyCheckInFeeCalc + lateCheckOutFeeCalc;

      let calculatedDiscountAmount = 0;
      if (discountType === "fixed" && discountValue > 0) {
        calculatedDiscountAmount = discountValue;
      } else if (discountType === "percentage" && discountValue > 0) {
        calculatedDiscountAmount = Math.round(total1 * discountValue / 100);
      }

      const total2 = Math.max(0, total1 - calculatedDiscountAmount);

      let serviceChargeCalc = 0;
      if (serviceChargeType === "fixed" && serviceChargeValue > 0) {
        serviceChargeCalc = serviceChargeValue;
      } else if (serviceChargeType === "percentage" && serviceChargeValue > 0) {
        serviceChargeCalc = Math.round(total2 * serviceChargeValue / 100);
      }

      const vatRateForCalc = values.enable_vat === "yes" ? Number(values.vat_percentage || 0) : 0;
      const total3 = total2 + serviceChargeCalc;
      const taxValueForCalc = Math.round(total3 * (vatRateForCalc / 100));
      const finalTotal = total3 + taxValueForCalc;

      const isBulk = enrichedRooms.length > 1 || enrichedRooms.some((r: any) => r.quantity > 1);

      if (isBulk) {
        const payload: any = {
          guest_name: values.guest_name,
          email: values.email,
          phone: values.phone,
          address_line_1: values.address_line_1,
          address_line_2: values.address_line_2,
          check_in_date: checkInFormatted,
          check_out_date: checkOutFormatted,
          reservation_source: values.reservation_source,
          special_requests: values.special_requests,
          id_document_type: values.id_document_type,
          id_document_number: values.id_document_number,
          early_check_in: !!values.early_check_in,
          late_check_out: !!values.late_check_out,
          early_check_in_fee: earlyCheckInFeeCalc,
          late_check_out_fee: lateCheckOutFeeCalc,
          allocation_note: values.allocation_note,
          vat_rate: values.enable_vat === "yes" ? Number(values.vat_percentage || 0) : 0,
          service_charge: serviceChargeCalc,
          service_charge_percentage: serviceChargeType === "percentage" ? serviceChargeValue : 0,
          discount_amount: calculatedDiscountAmount,
          discount_percentage: discountType === "percentage" ? discountValue : 0,
          rooms: enrichedRooms.map((r: any) => {
            const activeOffer = getBestActiveOffer(r.room_type_id, checkInFormatted, r.ac_status);
            return {
              room_type_id: r.room_type_id,
              quantity: r.quantity || 1,
              extra_beds: r.extra_beds || 0,
              number_of_guests: r.number_of_guests || 1,
              offerId: activeOffer ? activeOffer.id : null,
              ac_status: r.ac_status,
            };
          }),
          room_package_id: values.room_package_id,
          late_arrival: !!values.late_arrival,
          airport_pickup: !!values.airport_pickup,
          airport_pickup_time: values.airport_pickup_time || null,
          deposit_amount: (values.deposit_amount_input !== undefined && values.deposit_amount_input !== null && values.deposit_amount_input !== "")
            ? Number(values.deposit_amount_input)
            : undefined,
        };
        await frontDeskService.createBulkBookingsV2(payload);
      } else {
        const payload: any = {
          guest_name: values.guest_name,
          email: values.email,
          phone: values.phone,
          address_line_1: values.address_line_1,
          address_line_2: values.address_line_2,
          check_in_date: checkInFormatted,
          check_out_date: checkOutFormatted,
          room_type_id: firstRoom?.room_type_id,
          number_of_guests: firstRoom?.number_of_guests || 1,
          reservation_source: values.reservation_source,
          special_requests: values.special_requests,
          id_document_type: values.id_document_type,
          id_document_number: values.id_document_number,
          id_document_url: values.id_document_url,
          early_check_in: !!values.early_check_in,
          late_check_out: !!values.late_check_out,
          early_check_in_fee: earlyCheckInFeeCalc,
          late_check_out_fee: lateCheckOutFeeCalc,
          allocation_note: values.allocation_note,
          extra_beds: Number(firstRoom?.extra_beds || 0),
          vat_rate: values.enable_vat === "yes" ? Number(values.vat_percentage || 0) : 0,
          service_charge: serviceChargeCalc,
          service_charge_percentage: serviceChargeType === "percentage" ? serviceChargeValue : 0,
          discount_amount: calculatedDiscountAmount,
          discount_percentage: discountType === "percentage" ? discountValue : 0,
          ac_status: firstRoom?.ac_status,
          room_package_id: values.room_package_id,
          late_arrival: !!values.late_arrival,
          airport_pickup: !!values.airport_pickup,
          airport_pickup_time: values.airport_pickup_time || null,
          deposit_amount: (values.deposit_amount_input !== undefined && values.deposit_amount_input !== null && values.deposit_amount_input !== "")
            ? Number(values.deposit_amount_input)
            : undefined,
        };

        const activeOffer = getBestActiveOffer(firstRoom?.room_type_id, checkInFormatted, firstRoom?.ac_status);

        if (activeOffer) {
          payload.offerId = activeOffer.id;
        }
        
        await frontDeskService.createBookingV2(payload);
      }
      message.success("Front desk booking created");
      setCreateModalOpen(false);
      setCreateActiveTab("1");
      createForm.resetFields();
      createForm.setFieldsValue({
        rooms: [{ room_type_id: undefined, quantity: 1, extra_beds: 0 }],
      });
      fetchBookings();
    } catch (error: any) {
      console.error(error);
      if (
        error.response?.status === 409 &&
        error.response?.data?.alternatives
      ) {
        const alternatives = error.response.data.alternatives;
        Modal.error({
          title: "Selected Dates Occupied / Fully Booked",
          width: 550,
          content: (
            <div>
              <p style={{ marginBottom: "15px" }}>
                All rooms of your selected type are occupied on these dates.
                However, here are the available alternatives:
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {alternatives.length > 0 ? (
                  alternatives.map((alt: any) => (
                    <div
                      key={alt.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 15px",
                        backgroundColor: "#f8fbf9",
                        border: "1px solid #e2ece6",
                        borderRadius: "8px",
                      }}
                    >
                      <div>
                        <strong style={{ color: "#2e7d32" }}>{alt.name}</strong>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          👤 Max: {alt.max_occupancy} guests |{" "}
                          {alt.available_count}{" "}
                          {alt.available_count === 1 ? "room" : "rooms"} free
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span
                          style={{ fontWeight: "bold", marginRight: "10px" }}
                        >
                          LKR{" "}
                          {Number(alt.base_price).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                        <Button
                          type="primary"
                          size="small"
                          style={{
                            backgroundColor: "#2e7d32",
                            borderColor: "#2e7d32",
                          }}
                          onClick={() => {
                            createForm.setFieldsValue({ room_type_id: alt.id });
                            Modal.destroyAll();
                          }}
                        >
                          Select
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#888",
                    }}
                  >
                    No other room types are available for the selected dates.
                  </div>
                )}
              </div>
            </div>
          ),
          okText: "Close",
        });
      } else {
        message.error(
          error.response?.data?.message ||
            error?.message ||
            "Failed to create booking",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAllocate = useCallback(
    (booking: FrontDeskBooking) => {
      setSelectedBooking(booking);
      allocateForm.setFieldsValue({
        mode: "auto",
        room_id: undefined,
        note: booking.allocation_note,
      });
      setAllocateModalOpen(true);
    },
    [allocateForm],
  );

  const handleAllocate = async (values: any) => {
    if (!selectedBooking) return;
    const payload = {
      room_id: values.mode === "manual" ? values.room_id : undefined,
      note: values.note,
    };
    try {
      setLoading(true);
      await frontDeskService.allocateRoom(selectedBooking.id, payload);
      message.success("Room allocation completed");
      setAllocateModalOpen(false);
      fetchBookings();
    } catch (error: any) {
      console.error(error);
      if (isRoomUnavailable(error) && canForceOverlap()) {
        const reason = await promptForceReason("allocate this room");
        if (reason) {
          try {
            await frontDeskService.allocateRoom(selectedBooking.id, {
              ...payload,
              force: true,
              force_reason: reason,
            });
            message.success("Room allocated with force override");
            setAllocateModalOpen(false);
            fetchBookings();
          } catch (retryError: any) {
            message.error(getErrorMessage(retryError));
          }
        }
      } else {
        message.error(getErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCheckIn = useCallback(
    (booking: FrontDeskBooking) => {
      setSelectedCheckInBooking(booking);
      setCheckInModalOpen(true);
    },
    [checkInForm],
  );

  const handleCheckInSubmit = async (values: any) => {
    if (!selectedCheckInBooking) return;
    try {
      setLoading(true);
      await frontDeskService.checkIn(selectedCheckInBooking.id, {
        pay_remaining: !!values.pay_remaining,
        payment_method: values.payment_method,
        transaction_id: values.transaction_id,
        room_package_id: selectedCheckInBooking.room_package_id ?? undefined,
        // Receipt Type / VAT were removed from the popup — derive them from the
        // booking so check-in still recomputes the total exactly as before.
        receipt_type:
          Number(selectedCheckInBooking.vat_rate || 0) > 0 ? "vat" : "vat_free",
        vat_rate: Number(selectedCheckInBooking.vat_rate || 0),
        early_check_in: !!values.early_check_in,
        early_check_in_fee: values.early_check_in
          ? Number(values.early_check_in_fee || 0)
          : 0,
      });
      message.success(`Checked in ${selectedCheckInBooking.guest_name}`);
      setCheckInModalOpen(false);
      setSelectedCheckInBooking(null);
      checkInForm.resetFields();
      fetchBookings();
      fetchDashboard();
      
    } catch (error: any) {
      console.error(error);
      message.error(error?.message || "Failed to check in");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = useCallback(async (bookingId: string, payload: any) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const baseUrl =
        process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";

      const v2BaseUrl = baseUrl.replace('/api/v1', '/api/v2');
      const response = await fetch(`${v2BaseUrl}/bookings/invoice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to generate receipt PDF");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error: any) {
      console.error(error);
      message.error("Failed to load receipt PDF");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDatePrint = useCallback(async (params: { date?: string }) => {
    if (!printModalType) return;
    setPrintLoading(true);
    try {
      const date = params.date || dayjs().format("YYYY-MM-DD");
      let blob: Blob;
      let filename: string;
      if (printModalType === "arrivals") {
        blob = await reservationPrintService.getArrivalListPdf(date);
        filename = `arrival-list-${date}.pdf`;
      } else {
        blob = await reservationPrintService.getDepartureListPdf(date);
        filename = `departure-list-${date}.pdf`;
      }
      reservationPrintService.downloadPdf(blob, filename);
      message.success(`${printModalType === "arrivals" ? "Arrival" : "Departure"} list downloaded`);
    } catch (err) {
      message.error("Failed to generate PDF");
    } finally {
      setPrintLoading(false);
      setPrintModalType(null);
    }
  }, [printModalType]);

  const handleOpenCheckOut = useCallback(
    (booking: FrontDeskBooking) => {
      setSelectedCheckOutBooking(booking);
      checkOutForm.setFieldsValue({
        payment_method: "cash",
      });
      setCheckOutModalOpen(true);
    },
    [checkOutForm],
  );

  const handleCheckOut = async (values: any) => {
    if (!selectedCheckOutBooking) return;

    try {
      setLoading(true);
      const response: any = await frontDeskService.checkOut(
        selectedCheckOutBooking.id,
        {
          payment_method: values.payment_method,
          transaction_id: values.transaction_id || undefined,
          settle_balance: values.settle_balance === true,
          allow_outstanding_balance: values.allow_outstanding_balance === true,
          outstanding_reason: values.outstanding_reason || undefined,
        },
      );
      const emailSent = response?.data?.email_sent;
      const emailError = response?.data?.email_error;

      message.success(`Checked out ${selectedCheckOutBooking.guest_name}`);

      if (emailSent === false) {
        message.warning(
          `Checkout completed, but receipt email was not sent${emailError ? `: ${emailError}` : ""}`,
        );
      } else if (emailSent === true) {
        message.success("Receipt email sent to guest");
      }

      setCheckOutModalOpen(false);
      setSelectedCheckOutBooking(null);
      checkOutForm.resetFields();
      fetchBookings();
      fetchDashboard();
    } catch (error: any) {
      console.error(error);
      message.error(error?.message || "Failed to check out");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenExtendStay = useCallback(
    (booking: FrontDeskBooking) => {
      setSelectedExtendBooking(booking);
      setExtendStayModalOpen(true);
    },
    [],
  );

  const handleExtendStay = async (
    bookingId: string,
    newCheckOutDate: string,
  ) => {
    try {
      await frontDeskService.extendStay(bookingId, newCheckOutDate);
      message.success("Stay extended successfully");
      setExtendStayModalOpen(false);
      fetchBookings();
    } catch (error: any) {
      if (isRoomUnavailable(error) && canForceOverlap()) {
        const reason = await promptForceReason("extend the stay");
        if (reason) {
          try {
            await frontDeskService.extendStay(
              bookingId,
              newCheckOutDate,
              true,
              reason,
            );
            message.success("Stay extended with force override");
            setExtendStayModalOpen(false);
            fetchBookings();
          } catch (retryError: any) {
            message.error(getErrorMessage(retryError));
          }
        }
      } else {
        message.error(getErrorMessage(error));
      }
    }
  };

  const handleOpenChangeRoom = useCallback(
    (booking: FrontDeskBooking) => {
      setSelectedChangeRoomBooking(booking);
      setChangeRoomModalOpen(true);
    },
    [],
  );

  const handleChangeRoom = async (bookingId: string, roomId: string, reason?: string) => {
    try {
      await frontDeskService.changeRoom(bookingId, roomId, reason);
      message.success("Room changed successfully");
      setChangeRoomModalOpen(false);
      fetchBookings();
    } catch (error: any) {
      if (isRoomUnavailable(error) && canForceOverlap()) {
        const forceReason = await promptForceReason("move to this room");
        if (forceReason) {
          try {
            await frontDeskService.changeRoom(
              bookingId,
              roomId,
              reason,
              true,
              forceReason,
            );
            message.success("Room changed with force override");
            setChangeRoomModalOpen(false);
            fetchBookings();
          } catch (retryError: any) {
            message.error(getErrorMessage(retryError));
          }
        }
      } else {
        message.error(getErrorMessage(error));
      }
    }
  };

  const handleOpenCollectDeposit = useCallback(
    (booking: FrontDeskBooking) => {
      setSelectedDepositBooking(booking);
      setDepositModalOpen(true);
    },
    [],
  );

  const handleCollectDepositSuccess = async () => {
    await fetchBookings();
  };

  const computeManualRate = useCallback(
    (b: any): number | undefined => {
      const rt: any = roomTypes.find((x) => x.id === b?.room_type_id);
      if (!rt) return undefined;
      let standard = Number(rt.base_price || 0);
      if (rt.ac_status_preference === "both") {
        if (b.ac_status === "ac") standard = Number(rt.ac_base_price || rt.base_price);
        else if (b.ac_status === "non_ac") standard = Number(rt.non_ac_base_price || rt.base_price);
      }
      const eff = Number(b?.discounted_price || 0);
      const hasOffer = !!b?.offer_title;
      return !hasOffer && eff > 0 && eff !== standard ? eff : undefined;
    },
    [roomTypes],
  );

  const handleOpenEdit = useCallback(
    (booking: FrontDeskBooking & { groupChildren?: FrontDeskBooking[], is_group_parent?: boolean }) => {
      setSelectedEditBooking(booking);
      const existingDiscountAmt = Number(booking.discount_amount || 0);
      const existingDiscountPct = Number(booking.discount_percentage || 0);
      let discountType = "none";
      let discountValue = 0;
      if (existingDiscountPct > 0) {
        discountType = "percentage";
        discountValue = existingDiscountPct;
      } else if (existingDiscountAmt > 0) {
        discountType = "fixed";
        discountValue = existingDiscountAmt;
      }

      const existingServiceChargeAmt = Number((booking as any).service_charge || 0);
      const existingServiceChargePct = Number((booking as any).service_charge_percentage || 0);
      let serviceChargeType = "none";
      let serviceChargeValue = 0;
      if (existingServiceChargePct > 0) {
        serviceChargeType = "percentage";
        serviceChargeValue = existingServiceChargePct;
      } else if (existingServiceChargeAmt > 0) {
        serviceChargeType = "fixed";
        serviceChargeValue = existingServiceChargeAmt;
      }

      let mappedRooms: any[] = [{ 
        room_type_id: booking.room_type_id, 
        quantity: 1, 
        extra_beds: booking.extra_beds || 0,
        number_of_guests: booking.number_of_guests || 1,
        original_room_price: (booking as any).original_room_price,
        discounted_price: (booking as any).discounted_price,
        offer_title: (booking as any).offer_title,
        ac_status: (booking as any).ac_status && (booking as any).ac_status !== 'both' ? (booking as any).ac_status : undefined
      }];
      
      if (booking.is_group_parent && booking.groupChildren && booking.groupChildren.length > 0) {
        mappedRooms = booking.groupChildren.map(child => ({
          room_type_id: child.room_type_id, 
          quantity: 1, 
          extra_beds: child.extra_beds || 0,
          number_of_guests: child.number_of_guests || 1,
          original_room_price: (child as any).original_room_price,
          discounted_price: (child as any).discounted_price,
          offer_title: (child as any).offer_title,
          ac_status: (child as any).ac_status && (child as any).ac_status !== 'both' ? (child as any).ac_status : undefined
        }));
      }

      editForm.setFieldsValue({
        ...booking,
        nightly_rate: computeManualRate(booking),
        check_in_date: booking.check_in_date
          ? dayjs(booking.check_in_date)
          : undefined,
        check_out_date: booking.check_out_date
          ? dayjs(booking.check_out_date)
          : undefined,
        discount_type: discountType,
        discount_value: discountValue || undefined,
        service_charge_type: serviceChargeType,
        service_charge_value: serviceChargeValue || undefined,
        enable_vat: Number(booking.vat_rate || 0) > 0 ? "yes" : "no",
        vat_percentage: Number(booking.vat_rate || 0) > 0 ? Number(booking.vat_rate) : undefined,
        rooms: mappedRooms,
        deposit_amount_input: (booking as any).deposit_amount || undefined,
      });
      setEditModalOpen(true);
    },
    [editForm, computeManualRate],
  );

  const handleEditSubmit = async (values: any) => {
    if (!selectedEditBooking) return;
    try {
      setLoading(true);
      const discountType = values.discount_type || "none";
      const discountValue = Number(values.discount_value || 0);
      const checkInFormatted = values.check_in_date ? values.check_in_date.format("YYYY-MM-DD") : undefined;
      const checkOutFormatted = values.check_out_date ? values.check_out_date.format("YYYY-MM-DD") : undefined;

      const manualOverride =
        values.nightly_rate !== undefined &&
        values.nightly_rate !== null &&
        values.nightly_rate !== ""
          ? Number(values.nightly_rate)
          : undefined;

      const enrichedRooms = (values.rooms || []).map((r: any) => {
        const roomType = roomTypes.find((rt) => rt.id === r.room_type_id);
        const originalPrice = r.ac_status === 'ac'
          ? Number(roomType?.ac_base_price || roomType?.base_price || 0)
          : r.ac_status === 'non_ac'
            ? Number(roomType?.non_ac_base_price || roomType?.base_price || 0)
            : Number(roomType?.base_price || 0);
        let discountedPrice = originalPrice;
        let offerTitle: string | null = null;

        if (manualOverride !== undefined) {
          discountedPrice = manualOverride;
        } else if (checkInFormatted) {
          const activeOffer = getBestActiveOffer(r.room_type_id, checkInFormatted, r.ac_status);

          if (activeOffer) {
            if (activeOffer.discount_amount) {
              discountedPrice = Math.max(0, discountedPrice - Number(activeOffer.discount_amount));
            } else if (activeOffer.discount_percentage) {
              discountedPrice = Math.round(discountedPrice * (1 - Number(activeOffer.discount_percentage) / 100));
            }
            offerTitle = activeOffer.title;
          }
        }

        return {
          ...r,
          original_room_price: originalPrice,
          discounted_price: discountedPrice,
          offer_title: offerTitle,
        };
      });

      const firstRoom = enrichedRooms[0];
      
      const serviceChargeType = values.service_charge_type || "none";
      const serviceChargeValue = Number(values.service_charge_value || 0);

      let calculatedDiscountAmount = 0;
      let earlyCheckInFeeCalc = Number(values.early_check_in_fee || 0);
      let lateCheckOutFeeCalc = Number(values.late_check_out_fee || 0);
      let serviceChargeCalc = 0;
      let finalTotalEdit = 0;

      if (checkInFormatted && checkOutFormatted) {
        const checkInDateObj = dayjs(checkInFormatted);
        const checkOutDateObj = dayjs(checkOutFormatted);
        const nights = Math.max(checkOutDateObj.diff(checkInDateObj, "days"), 1);

        let roomSubtotalForCalc = 0;
        enrichedRooms.forEach((r: any) => {
          const roomTypeForCalc = roomTypes.find((rt) => rt.id === r.room_type_id);
          const roomTotalForCalc = (r.discounted_price || 0) * nights * (r.quantity || 1);
          const extraBedsTotalForCalc = Number(r.extra_beds || 0) * Number(roomTypeForCalc?.extra_bed_price || 0) * nights;
          roomSubtotalForCalc += roomTotalForCalc + extraBedsTotalForCalc;
        });

        const total1 = roomSubtotalForCalc + earlyCheckInFeeCalc + lateCheckOutFeeCalc;

        let packageChargeForCalc = 0;
        if (checkInFormatted && checkOutFormatted) {
          const selectedPkg = roomPackages.find(
            (p) => p.id === values.room_package_id,
          );
          if (selectedPkg) {
            const totalGuestsForPkg = enrichedRooms.reduce(
              (sum: number, r: any) =>
                sum + Number(r?.number_of_guests || 1) * Number(r?.quantity || 1),
              0,
            );
            const pcBase = Number(selectedPkg.base_price || 0);
            const pcPerPerson = Number(selectedPkg.price_per_person || 0);
            const pcG = Math.max(totalGuestsForPkg || 1, 1);
            const pcN = Math.max(nights || 1, 1);
            if (selectedPkg.charge_basis === "per_room_per_night")
              packageChargeForCalc = pcBase * pcN;
            else if (selectedPkg.charge_basis === "per_stay")
              packageChargeForCalc = pcBase;
            else if (selectedPkg.charge_basis === "per_person_per_stay")
              packageChargeForCalc = pcPerPerson * pcG;
            else if (selectedPkg.charge_basis === "per_person_per_night")
              packageChargeForCalc = pcPerPerson * pcG * pcN;
          }
        }
        const totalForCalc =
          roomSubtotalForCalc +
          packageChargeForCalc +
          earlyCheckInFeeCalc +
          lateCheckOutFeeCalc;

        if (discountType === "fixed" && discountValue > 0) {
          calculatedDiscountAmount = discountValue;
        } else if (discountType === "percentage" && discountValue > 0) {
          calculatedDiscountAmount = Math.round(totalForCalc * discountValue / 100);
        }

        const total2 = Math.max(0, totalForCalc - calculatedDiscountAmount);

        if (serviceChargeType === "fixed" && serviceChargeValue > 0) {
          serviceChargeCalc = serviceChargeValue;
        } else if (serviceChargeType === "percentage" && serviceChargeValue > 0) {
          serviceChargeCalc = Math.round(total2 * serviceChargeValue / 100);
        }

        const vatRateForCalc = values.enable_vat === "yes" ? Number(values.vat_percentage || 0) : 0;
        const total3Edit = total2 + serviceChargeCalc;
        const taxValueForCalc = Math.round(total3Edit * (vatRateForCalc / 100));
        finalTotalEdit = total3Edit + taxValueForCalc;
      }

      const payload: any = {
        guest_name: values.guest_name,
        email: values.email,
        phone: values.phone,
        address_line_1: values.address_line_1,
        address_line_2: values.address_line_2,
        check_in_date: checkInFormatted,
        check_out_date: checkOutFormatted,
        room_type_id: firstRoom?.room_type_id,
        number_of_guests: firstRoom?.number_of_guests || 1,
        reservation_source: values.reservation_source,
        special_requests: values.special_requests,
          id_document_type: values.id_document_type,
          id_document_number: values.id_document_number,
          id_document_url: values.id_document_url,
          early_check_in: !!values.early_check_in,
        late_check_out: !!values.late_check_out,
        early_check_in_fee: earlyCheckInFeeCalc,
        late_check_out_fee: lateCheckOutFeeCalc,
        allocation_note: values.allocation_note,
        extra_beds: Number(firstRoom?.extra_beds || 0),
        vat_rate: values.enable_vat === "yes" ? Number(values.vat_percentage || 0) : 0,
        service_charge: serviceChargeCalc,
        service_charge_percentage: serviceChargeType === "percentage" ? serviceChargeValue : 0,
        discount_amount: calculatedDiscountAmount,
        discount_percentage: discountType === "percentage" ? discountValue : 0,
        ac_status: firstRoom?.ac_status,
        room_package_id: values.room_package_id,
        late_arrival: !!values.late_arrival,
        airport_pickup: !!values.airport_pickup,
        airport_pickup_time: values.airport_pickup_time || null,
        payment_plan: values.payment_plan || "deposit",
        deposit_amount: (values.deposit_amount_input !== undefined && values.deposit_amount_input !== null && values.deposit_amount_input !== "")
          ? Number(values.deposit_amount_input)
          : undefined,
      };

      if (manualOverride !== undefined) {
        payload.nightly_rate = manualOverride;
      } else if (computeManualRate(selectedEditBooking) !== undefined) {
        payload.nightly_rate = null;
      }

      if (checkInFormatted) {
        const now = new Date();
        const activeOffer = getBestActiveOffer(firstRoom?.room_type_id, now, firstRoom?.ac_status);

        payload.offerId = activeOffer ? activeOffer.id : null;
      }
      
      const isBulkUpdate = enrichedRooms.length > 1 || enrichedRooms.some((r: any) => r.quantity > 1) || ((selectedEditBooking as any).is_group_parent && selectedEditBooking.group_id);

      const sendEditUpdate = async (forceReason?: string) => {
      if (isBulkUpdate) {
         const targetId = selectedEditBooking.group_id ? selectedEditBooking.group_id : selectedEditBooking.id;
         const groupPayload: any = {
          guest_name: values.guest_name,
          email: values.email,
          phone: values.phone,
          address_line_1: values.address_line_1,
          address_line_2: values.address_line_2,
          reservation_source: values.reservation_source,
          check_in_date: checkInFormatted,
          check_out_date: checkOutFormatted,
          early_check_in: !!values.early_check_in,
          late_check_out: !!values.late_check_out,
          early_check_in_fee: earlyCheckInFeeCalc,
          late_check_out_fee: lateCheckOutFeeCalc,
          allocation_note: values.allocation_note,
          vat_rate: values.enable_vat === "yes" ? Number(values.vat_percentage || 0) : 0,
          service_charge: serviceChargeCalc,
          service_charge_percentage: serviceChargeType === "percentage" ? serviceChargeValue : 0,
          discount_amount: calculatedDiscountAmount,
          discount_percentage: discountType === "percentage" ? discountValue : 0,
          room_package_id: values.room_package_id,
          late_arrival: !!values.late_arrival,
          airport_pickup: !!values.airport_pickup,
          airport_pickup_time: values.airport_pickup_time || null,
          deposit_amount: (values.deposit_amount_input !== undefined && values.deposit_amount_input !== null && values.deposit_amount_input !== "")
            ? Number(values.deposit_amount_input)
            : undefined,
          rooms: enrichedRooms.map((r: any) => {
            const now = new Date();
            const activeOffer = getBestActiveOffer(r.room_type_id, now, r.ac_status);
            return {
              room_type_id: r.room_type_id,
              quantity: r.quantity || 1,
              extra_beds: r.extra_beds || 0,
              number_of_guests: r.number_of_guests || 1,
              offerId: activeOffer ? activeOffer.id : null,
              ac_status: r.ac_status,
            };
          }),
        };
        await frontDeskService.updateBulkBookingV2(targetId, forceReason ? { ...groupPayload, force: true, force_reason: forceReason } : groupPayload);
      } else {
         await frontDeskService.updateBookingV2(selectedEditBooking.id, forceReason ? { ...payload, force: true, force_reason: forceReason } : payload);
      }
      };

      try {
        await sendEditUpdate();
      } catch (updateError: any) {
        if (isRoomUnavailable(updateError) && canForceOverlap()) {
          const forceReason = await promptForceReason("save these changes");
          if (!forceReason) throw updateError;
          await sendEditUpdate(forceReason);
        } else {
          throw updateError;
        }
      }

      message.success("Booking updated");
      setEditModalOpen(false);
      setEditActiveTab("1");
      setSelectedEditBooking(null);
      editForm.resetFields();
      fetchBookings();
    } catch (error: any) {
      console.error(error);
      message.error(error?.message || "Failed to update booking");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenView = useCallback(
    (booking: FrontDeskBooking & { groupChildren?: FrontDeskBooking[], is_group_parent?: boolean, is_group_child?: boolean }) => {
      if (booking.is_group_child && booking.group_id) {
        const parentGroup = groupedBookings.find((b: any) => b.is_group_parent && b.group_id === booking.group_id);
        if (parentGroup) {
          setSelectedViewBooking(parentGroup as any);
          setViewModalOpen(true);
          return;
        }
      }
      setSelectedViewBooking(booking);
      setViewModalOpen(true);
    },
    [groupedBookings]
  );

  const handleDashboardAction = useCallback(
    async (bookingId: string, action: "checkin" | "checkout" | "view") => {
      let booking = bookings.find((b) => b.id === bookingId);
      if (!booking) {
        try {
          const res = await frontDeskService.getBookings({});
          const list: FrontDeskBooking[] = Array.isArray(res.data) ? res.data : [];
          booking = list.find((b) => b.id === bookingId);
        } catch {
          /* fall through */
        }
      }
      if (!booking) {
        message.error("Booking not found");
        return;
      }
      if (action === "checkin") handleOpenCheckIn(booking);
      else if (action === "checkout") handleOpenCheckOut(booking);
      else handleOpenView(booking);
    },
    [bookings, handleOpenCheckIn, handleOpenCheckOut, handleOpenView]
  );

  const handleDeleteBooking = useCallback(
    (booking: FrontDeskBooking) => {
      Modal.confirm({
        title: "Delete Booking",
        content: `Are you sure you want to delete the booking for ${booking.guest_name}?`,
        okText: "Yes",
        cancelText: "No",
        onOk: async () => {
          try {
            setLoading(true);
            await frontDeskService.deleteBooking(booking.id);
            message.success("Booking deleted");
            fetchBookings();
          } catch (error: any) {
            console.error(error);
            message.error(error?.message || "Failed to delete booking");
          } finally {
            setLoading(false);
          }
        },
      });
    },
    [fetchBookings],
  );

  const handleNoShow = useCallback(
    (booking: FrontDeskBooking) => {
      Modal.confirm({
        title: "Mark as No-Show",
        content: `Mark the booking for ${booking.guest_name} as no-show? The room hold is released and the status cannot be changed afterwards.`,
        okText: "Mark No-Show",
        cancelText: "Cancel",
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await adminBookingService.markNoShow(booking.id);
            message.success("Booking marked as no-show");
            fetchBookings();
          } catch (error: any) {
            console.error("Error marking no-show:", error);
            message.error(
              error?.response?.data?.message || "Failed to mark no-show",
            );
          }
        },
      });
    },
    [fetchBookings],
  );

  const bookingCols = useMemo(
    () =>
      getBookingColumns({
        handlePrintReceipt,
        handleOpenAllocate,
        handleOpenEdit,
        handleOpenCheckIn,
        handleOpenCheckOut,
        handleDeleteBooking,
        handleOpenView,
        handleExtendStay: handleOpenExtendStay,
        handleChangeRoom: handleOpenChangeRoom,
        handleCollectDeposit: handleOpenCollectDeposit,
        handleNoShow,
      }),
    [
      handlePrintReceipt,
      handleOpenAllocate,
      handleOpenEdit,
      handleOpenCheckIn,
      handleOpenCheckOut,
      handleDeleteBooking,
      handleOpenView,
      handleOpenExtendStay,
      handleOpenChangeRoom,
      handleOpenCollectDeposit,
      handleNoShow,
    ],
  );

  const guestCols = useMemo(() => getGuestColumns(), []);

  return (
    <div className="front-desk-container">
      <div className="front-desk-header">
        <h1>Front Desk / Reception</h1>
        <p>
          Manage walk-ins, reservations, room allocation, check-ins/outs and
          guest profiles.
        </p>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "dashboard",
            label: "Dashboard",
            children: (
              <div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
                  <Card size="small" title="Today's Arrivals" style={{ flex: "1 1 200px" }}>
                    <div style={{ fontSize: 30, fontWeight: 600, color: "#1677ff" }}>
                      {dashboard?.arrivals?.length || 0}
                    </div>
                  </Card>
                  <Card size="small" title="Today's Departures" style={{ flex: "1 1 200px" }}>
                    <div style={{ fontSize: 30, fontWeight: 600, color: "#fa8c16" }}>
                      {dashboard?.departures?.length || 0}
                    </div>
                  </Card>
                  <Card size="small" title="In-House Guests" style={{ flex: "1 1 200px" }}>
                    <div style={{ fontSize: 30, fontWeight: 600, color: "#52c41a" }}>
                      {dashboard?.in_house || 0}
                    </div>
                  </Card>
                  <Card size="small" title="Outstanding Balance" style={{ flex: "1 1 200px" }}>
                    <div style={{ fontSize: 30, fontWeight: 600, color: "#ff4d4f" }}>
                      LKR{" "}
                      {Number(dashboard?.outstanding?.total || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </div>
                    <div style={{ color: "#888", fontSize: 12 }}>
                      {dashboard?.outstanding?.count || 0} open folio(s)
                    </div>
                  </Card>
                </div>

                <Card
                  size="small"
                  title={`Room Status — ${dashboard?.date || ""}`}
                  style={{ marginBottom: 16 }}
                  extra={
                    <Button size="small" onClick={fetchDashboard} loading={dashboardLoading}>
                      Refresh
                    </Button>
                  }
                >
                  <Space wrap>
                    <Tag color="green">Available {dashboard?.room_status?.available ?? 0}</Tag>
                    <Tag color="orange">Occupied {dashboard?.room_status?.occupied ?? 0}</Tag>
                    <Tag color="geekblue">Cleaning {dashboard?.room_status?.cleaning ?? 0}</Tag>
                    <Tag color="gold">Maintenance {dashboard?.room_status?.maintenance ?? 0}</Tag>
                    <Tag color="red">Out of Order {dashboard?.room_status?.out_of_order ?? 0}</Tag>
                    <Tag>Total {dashboard?.room_status?.total ?? 0}</Tag>
                  </Space>
                </Card>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <Card size="small" title="Arrivals" style={{ flex: "1 1 360px" }}>
                    <Table
                      size="small"
                      pagination={false}
                      rowKey="id"
                      loading={dashboardLoading}
                      dataSource={dashboard?.arrivals || []}
                      locale={{ emptyText: "No arrivals today" }}
                      columns={[
                        { title: "Guest", dataIndex: "guest_name", ellipsis: true },
                        {
                          title: "Room",
                          dataIndex: "room_number",
                          width: 70,
                          render: (v: any) => v || "-",
                        },
                        {
                          title: "",
                          key: "action",
                          width: 95,
                          align: "right" as const,
                          render: (_: any, row: any) => (
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => handleDashboardAction(row.id, "checkin")}
                            >
                              Check-In
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </Card>

                  <Card size="small" title="Departures" style={{ flex: "1 1 360px" }}>
                    <Table
                      size="small"
                      pagination={false}
                      rowKey="id"
                      loading={dashboardLoading}
                      dataSource={dashboard?.departures || []}
                      locale={{ emptyText: "No departures today" }}
                      columns={[
                        { title: "Guest", dataIndex: "guest_name", ellipsis: true },
                        {
                          title: "Room",
                          dataIndex: "room_number",
                          width: 70,
                          render: (v: any) => v || "-",
                        },
                        {
                          title: "",
                          key: "action",
                          width: 95,
                          align: "right" as const,
                          render: (_: any, row: any) => (
                            <Button
                              size="small"
                              onClick={() => handleDashboardAction(row.id, "checkout")}
                            >
                              Check-Out
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </Card>

                  <Card
                    size="small"
                    title={`Outstanding Balances (Top ${dashboard?.outstanding?.top?.length || 0})`}
                    style={{ flex: "1 1 360px" }}
                  >
                    <Table
                      size="small"
                      pagination={false}
                      rowKey="id"
                      loading={dashboardLoading}
                      dataSource={dashboard?.outstanding?.top || []}
                      locale={{ emptyText: "No outstanding balances" }}
                      columns={[
                        { title: "Guest", dataIndex: "guest_name", ellipsis: true },
                        {
                          title: "Room",
                          dataIndex: "room_number",
                          width: 70,
                          render: (v: any) => v || "-",
                        },
                        {
                          title: "Balance",
                          dataIndex: "balance",
                          align: "right" as const,
                          render: (v: any) => (
                            <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
                              LKR{" "}
                              {Number(v || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}
                            </span>
                          ),
                        },
                        {
                          title: "",
                          key: "action",
                          width: 70,
                          align: "right" as const,
                          render: (_: any, row: any) => (
                            <Button
                              size="small"
                              onClick={() => handleDashboardAction(row.id, "view")}
                            >
                              View
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </Card>
                </div>
              </div>
            ),
          },
          {
            key: "bookings",
            label: "Front Desk Operations",
            children: (
              <>
                <div className="front-desk-toolbar">
                  <Space wrap>
                    <Input
                      placeholder="Search name/email/phone/room/conf #"
                      allowClear
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ width: 280 }}
                    />
                    <Select
                      placeholder="Filter status"
                      allowClear
                      value={statusFilter}
                      onChange={setStatusFilter}
                      style={{ width: 180 }}
                      options={[
                        { label: "Pending", value: "pending" },
                        { label: "Confirmed", value: "confirmed" },
                        { label: "Checked-In", value: "checked_in" },
                        { label: "Checked-Out", value: "checked_out" },
                        { label: "Cancelled", value: "cancelled" },
                        { label: "Reserved", value: "reserved" },
                        { label: "Blocked", value: "blocked" },
                        { label: "No Show", value: "no_show" },
                      ]}
                    />
                    <RangePicker
                      value={bookingDateRange}
                      onChange={(dates) =>
                        setBookingDateRange(
                          dates as [dayjs.Dayjs, dayjs.Dayjs] | null,
                        )
                      }
                    />
                    <Button onClick={fetchBookings}>Search/Refresh</Button>
                  </Space>
                  <Space>
                    <Dropdown
                      menu={{
                        items: [
                          { key: "arrivals", label: "Print Arrival List", onClick: () => setPrintModalType("arrivals") },
                          { key: "departures", label: "Print Departure List", onClick: () => setPrintModalType("departures") },
                        ],
                      }}
                      trigger={["click"]}
                    >
                      <Button icon={<PrinterOutlined />}>Print Reports</Button>
                    </Dropdown>
                    <Button
                      type="primary"
                      onClick={() => setCreateModalOpen(true)}
                    >
                      New Walk-In / Reservation
                    </Button>
                  </Space>
                </div>

                <Table
                  rowKey={(record: any) => record.is_group_parent ? `group-${record.group_id}` : record.id}
                  childrenColumnName="groupChildren"
                  loading={loading}
                  columns={bookingCols}
                  dataSource={groupedBookings}
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 1400 }}
                  rowClassName={(record: any) => {
                    if (record.is_group_parent) return 'group-parent-row';
                    if (record.is_group_child) return 'group-child-row';
                    return '';
                  }}
                  onRow={(record) => ({
                    onClick: () => {
                      handleOpenView(record);
                    },
                    style: { cursor: 'pointer' },
                  })}
                />
              </>
            ),
          },
          {
            key: "availability",
            label: "Room Availability Calendar",
            children: <ReservationCalendar />,
          },
          {
            key: "guestProfiles",
            label: "Guest Profiles",
            children: (
              <>
                <div className="front-desk-toolbar">
                  <Space wrap>
                    <Input
                      placeholder="Search guest profile"
                      allowClear
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ width: 280 }}
                    />
                    <Button onClick={fetchGuestProfiles}>Search/Refresh</Button>
                  </Space>
                </div>

                <Table
                  rowKey="id"
                  loading={loading}
                  columns={guestCols}
                  dataSource={guestProfiles}
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 1200 }}
                  onRow={(record) => ({
                    onClick: () => {
                      setSelectedGuestProfile(record);
                      setViewGuestModalOpen(true);
                    },
                    style: { cursor: 'pointer' },
                  })}
                />
              </>
            ),
          },
        ]}
      />

      <CreateBookingModal
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setCreateActiveTab("1");
        }}
        form={createForm}
        activeTab={createActiveTab}
        setActiveTab={setCreateActiveTab}
        onSubmit={handleCreateBooking}
        loading={loading}
        isCreateValid={isCreateValid}
        roomTypes={roomTypes}
        roomAvailabilities={roomAvailabilities}
        blockedDates={blockedDates}
        createCheckInDate={createCheckInDate}
        createCheckOutDate={createCheckOutDate}
        roomsList={roomsList}
        offers={offers}
        rooms={rooms}
        guestProfiles={guestProfiles}
        roomPackages={roomPackages}
      />

      <EditBookingModal
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setEditActiveTab("1");
          setSelectedEditBooking(null);
          editForm.resetFields();
        }}
        form={editForm}
        activeTab={editActiveTab}
        setActiveTab={setEditActiveTab}
        onSubmit={handleEditSubmit}
        loading={loading}
        isEditValid={isEditValid.length === 0}
        editValidationErrors={isEditValid}
        selectedEditBooking={selectedEditBooking}
        roomTypes={roomTypes}
        editCheckInDate={editCheckInDate}
        editCheckOutDate={editCheckOutDate}
        roomAvailabilities={roomAvailabilities}
        blockedDates={blockedDates}
        editRoomsList={editRoomsList}
        offers={offers}
        guestProfiles={guestProfiles}
        rooms={rooms}
        roomPackages={roomPackages}
      />

      <CheckInModal
        open={checkInModalOpen}
        onCancel={() => {
          setCheckInModalOpen(false);
          setSelectedCheckInBooking(null);
          checkInForm.resetFields();
        }}
        form={checkInForm}
        onSubmit={handleCheckInSubmit}
        selectedCheckInBooking={selectedCheckInBooking}
        loading={loading}
      />

      <CheckOutModal
        open={checkOutModalOpen}
        onCancel={() => {
          setCheckOutModalOpen(false);
          setSelectedCheckOutBooking(null);
          checkOutForm.resetFields();
        }}
        form={checkOutForm}
        onSubmit={handleCheckOut}
        selectedCheckOutBooking={selectedCheckOutBooking}
        loading={loading}
      />

      <AllocateModal
        open={allocateModalOpen}
        onCancel={() => setAllocateModalOpen(false)}
        form={allocateForm}
        onSubmit={handleAllocate}
        selectedBooking={selectedBooking}
        rooms={rooms}
        bookings={bookings}
        loading={loading}
      />

      <ExtendStayModal
        open={extendStayModalOpen}
        booking={selectedExtendBooking}
        onCancel={() => setExtendStayModalOpen(false)}
        onExtend={handleExtendStay}
      />

      <ChangeRoomModal
        open={changeRoomModalOpen}
        booking={selectedChangeRoomBooking}
        onCancel={() => setChangeRoomModalOpen(false)}
        onChangeRoom={handleChangeRoom}
        rooms={rooms}
        roomTypes={roomTypes}
      />

      <DepositPaymentModal
        open={depositModalOpen}
        booking={selectedDepositBooking}
        onCancel={() => setDepositModalOpen(false)}
        onSuccess={handleCollectDepositSuccess}
      />

      <ViewBookingModal
        open={viewModalOpen}
        onCancel={() => setViewModalOpen(false)}
        booking={selectedViewBooking}
        roomTypes={roomTypes}
        offers={offers}
        onEdit={handleOpenEdit}
        onDelete={handleDeleteBooking}
        onPrintReceipt={handlePrintReceipt}
        onRoomCheck={(booking) => {
          setSelectedRoomCheckBooking(booking);
          setRoomCheckModalOpen(true);
        }}
      />

      <RoomCheckOutInspectionModal
        open={roomCheckModalOpen}
        onCancel={() => {
          setRoomCheckModalOpen(false);
          setSelectedRoomCheckBooking(null);
        }}
        onSuccess={() => fetchBookings()}
        booking={selectedRoomCheckBooking}
      />

      <ViewGuestProfileModal
        open={viewGuestModalOpen}
        guestProfile={selectedGuestProfile}
        onCancel={() => {
          setViewGuestModalOpen(false);
          setSelectedGuestProfile(null);
        }}
        onRefresh={fetchGuestProfiles}
        onViewBooking={handleOpenView}
      />

      <DatePrintModal
        open={!!printModalType}
        title={printModalType === "arrivals" ? "Print Arrival List" : "Print Departure List"}
        type="single"
        onPrint={handleDatePrint}
        onCancel={() => setPrintModalType(null)}
        loading={printLoading}
      />
    </div>
  );
};

export default FrontDesk;
